import React, { useEffect, useMemo, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { useDispatch, useSelector } from "react-redux";
import { EMPLOYEE_ACTION } from "../../redux/action";
import { Labelbox } from "../../components";
import { Toaster, dropdownNameWithEmail } from "../../utils/common";
import {
  buildEmployeeByIdMap,
  getAppreciationFromName,
  getAppreciationToName,
} from "./hrmsAppreciationUtils";
import {
  hrmsAdminCreateAnnouncement,
  hrmsAdminCreateAppreciation,
  hrmsAdminCreateEvent,
  hrmsAdminCreateHoliday,
  hrmsAdminDeleteAnnouncement,
  hrmsAdminDeleteAppreciation,
  hrmsAdminDeleteEvent,
  hrmsAdminDeleteHoliday,
} from "../../utils/hrmsApi";
import {
  parseDashboardRowDate,
  splitHolidaysByRegion,
} from "./hrmsDashboardFilters";

const tabs = ["Holidays", "Events", "Announcements", "Appreciations"];

const REGION_DROPDOWN = [
  { id: "IN", value: "India (IN)", keepAtTop: true },
  { id: "US", value: "United States (US)" },
];

const RESTRICTED_DROPDOWN = [
  { id: "0", value: "No — standard holiday", keepAtTop: true },
  { id: "1", value: "Yes — optional / restricted" },
];

function handleApiResult(res, fallbackErr, onOk) {
  const body = res?.data || {};
  if (res?.status === 200 && body.status === 1) {
    Toaster.success(body.message || "Saved.");
    onOk();
    return true;
  }
  Toaster.error(body.message || body.error || fallbackErr);
  return false;
}

function sortHolidaysByDate(rows) {
  return [...(rows || [])].sort((a, b) => {
    const ta = parseDashboardRowDate(a)?.getTime() ?? 0;
    const tb = parseDashboardRowDate(b)?.getTime() ?? 0;
    return ta - tb;
  });
}

export default function HrmsDashboardAdmin({ dashboardModel, onChanged }) {
  const dispatch = useDispatch();
  const activeEmployees = useSelector(
    (state) => state.employee?.activeEmployees || [],
  );

  const [adminTab, setAdminTab] = useState("Holidays");

  const [holidayForm, setHolidayForm] = useState({
    holidayDate: "",
    title: "",
    locationRegion: "IN",
    isRestrictedSelect: "0",
  });
  const [eventForm, setEventForm] = useState({
    eventDate: "",
    title: "",
    endDate: "",
    location: "",
  });
  const [announceForm, setAnnounceForm] = useState({ title: "", body: "" });
  const [apprForm, setApprForm] = useState({
    message: "",
    toEmployeeId: "",
    fromEmployeeId: "",
  });

  useEffect(() => {
    if (!Array.isArray(activeEmployees) || activeEmployees.length === 0) {
      dispatch(EMPLOYEE_ACTION.getActiveEmployees());
    }
  }, [dispatch, activeEmployees]);

  const holidays = dashboardModel?.holidays || [];
  const events = dashboardModel?.events || [];
  const announcements = dashboardModel?.announcements || [];
  const appreciations = dashboardModel?.appreciations || [];

  const { inList: refHolidaysIn, usList: refHolidaysUs } = useMemo(() => {
    const { inList, usList } = splitHolidaysByRegion(holidays);
    return {
      inList: sortHolidaysByDate(inList),
      usList: sortHolidaysByDate(usList),
    };
  }, [holidays]);

  const employeeSelectDropdown = useMemo(() => {
    const opts = [
      { id: "", value: "— Optional —", keepAtTop: true },
      ...(activeEmployees || []).map((data) => ({
        id: data.employee_id,
        value: dropdownNameWithEmail(data),
      })),
    ];
    return opts;
  }, [activeEmployees]);

  const employeeById = useMemo(
    () => buildEmployeeByIdMap(activeEmployees),
    [activeEmployees],
  );

  const boolRestricted = (v) => v === true || v === 1 || v === "1";

  const onHolidayCreate = async (e) => {
    e.preventDefault();
    if (!String(holidayForm.holidayDate || "").trim()) {
      Toaster.warning("Holiday date is required.");
      return;
    }
    if (!String(holidayForm.title || "").trim()) {
      Toaster.warning("Title is required.");
      return;
    }
    try {
      const res = await hrmsAdminCreateHoliday({
        holidayDate: holidayForm.holidayDate,
        title: holidayForm.title,
        locationRegion: holidayForm.locationRegion,
        isRestricted: holidayForm.isRestrictedSelect === "1",
      });
      if (
        handleApiResult(res, "Could not create holiday.", () => {
          setHolidayForm({
            holidayDate: "",
            title: "",
            locationRegion: holidayForm.locationRegion,
            isRestrictedSelect: "0",
          });
          onChanged();
        })
      );
    } catch (err) {
      Toaster.error(err?.response?.data?.message || err.message || "Error");
    }
  };

  const onEventCreate = async (e) => {
    e.preventDefault();
    try {
      const body = {
        eventDate: eventForm.eventDate,
        title: eventForm.title,
      };
      if (eventForm.endDate) body.endDate = eventForm.endDate;
      if (eventForm.location) body.location = eventForm.location;
      const res = await hrmsAdminCreateEvent(body);
      if (
        handleApiResult(res, "Could not create event.", () => {
          setEventForm({
            eventDate: "",
            title: "",
            endDate: "",
            location: "",
          });
          onChanged();
        })
      );
    } catch (err) {
      Toaster.error(err?.response?.data?.message || err.message || "Error");
    }
  };

  const onAnnounceCreate = async (e) => {
    e.preventDefault();
    if (!announceForm.body.trim()) {
      Toaster.warning("Body is required.");
      return;
    }
    try {
      const body = { body: announceForm.body };
      if (announceForm.title.trim()) body.title = announceForm.title.trim();
      const res = await hrmsAdminCreateAnnouncement(body);
      if (
        handleApiResult(res, "Could not create announcement.", () => {
          setAnnounceForm({ title: "", body: "" });
          onChanged();
        })
      );
    } catch (err) {
      Toaster.error(err?.response?.data?.message || err.message || "Error");
    }
  };

  const onApprCreate = async (e) => {
    e.preventDefault();
    if (!apprForm.message.trim()) {
      Toaster.warning("Message is required.");
      return;
    }
    try {
      const body = { message: apprForm.message.trim() };
      const toId =
        apprForm.toEmployeeId != null ? String(apprForm.toEmployeeId).trim() : "";
      if (toId) body.toEmployeeId = toId;
      const fromId =
        apprForm.fromEmployeeId != null
          ? String(apprForm.fromEmployeeId).trim()
          : "";
      if (fromId) body.fromEmployeeId = fromId;
      const res = await hrmsAdminCreateAppreciation(body);
      if (
        handleApiResult(res, "Could not create appreciation.", () => {
          setApprForm({ message: "", toEmployeeId: "", fromEmployeeId: "" });
          onChanged();
        })
      );
    } catch (err) {
      Toaster.error(err?.response?.data?.message || err.message || "Error");
    }
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    try {
      const res = await hrmsAdminDeleteHoliday(id);
      handleApiResult(res, "Delete failed.", onChanged);
    } catch (err) {
      Toaster.error(err?.response?.data?.message || err.message || "Error");
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      const res = await hrmsAdminDeleteEvent(id);
      handleApiResult(res, "Delete failed.", onChanged);
    } catch (err) {
      Toaster.error(err?.response?.data?.message || err.message || "Error");
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      const res = await hrmsAdminDeleteAnnouncement(id);
      handleApiResult(res, "Delete failed.", onChanged);
    } catch (err) {
      Toaster.error(err?.response?.data?.message || err.message || "Error");
    }
  };

  const deleteAppreciation = async (id) => {
    if (!window.confirm("Delete this appreciation?")) return;
    try {
      const res = await hrmsAdminDeleteAppreciation(id);
      handleApiResult(res, "Delete failed.", onChanged);
    } catch (err) {
      Toaster.error(err?.response?.data?.message || err.message || "Error");
    }
  };

  return (
    <div className="hrms-dashboard-admin">
      <h2 className="hrms-dashboard-admin__title">Admin - HRMS content</h2>
      <p className="hrms-dashboard-admin__hint">
        Create and manage holidays, events, announcements, and appreciations.
        Changes apply after save; the dashboard above refreshes automatically.
      </p>
      <div className="hrms-admin-tabs">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            className={`hrms-admin-tab ${adminTab === t ? "active" : ""}`}
            onClick={() => setAdminTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {adminTab === "Holidays" && (
        <div className="hrms-admin-panel">
          <form className="hrms-admin-form" onSubmit={onHolidayCreate}>
            <h4>New holiday</h4>
            <Grid
              container
              spacing={2}
              alignItems="stretch"
              className="hrms-admin-holiday-row"
            >
              <Grid item xs={12} sm={6} md={3}>
                <div className="hrms-admin-field-cell">
                  <Labelbox
                    type="datepicker"
                    labelname="Holiday date"
                    value={holidayForm.holidayDate || null}
                    changeData={(v) =>
                      setHolidayForm((s) => ({ ...s, holidayDate: v || "" }))
                    }
                    required
                    error={false}
                    errmsg={null}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div className="hrms-admin-field-cell">
                  <Labelbox
                    type="text"
                    labelname="Title"
                    value={holidayForm.title}
                    changeData={(v) =>
                      setHolidayForm((s) => ({ ...s, title: v }))
                    }
                    required
                    error={false}
                    errmsg={null}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div className="hrms-admin-field-cell">
                  <Labelbox
                    type="select"
                    labelname="Region"
                    dropdown={REGION_DROPDOWN}
                    value={holidayForm.locationRegion}
                    changeData={(v) =>
                      setHolidayForm((s) => ({ ...s, locationRegion: v }))
                    }
                    required
                    error={false}
                    errmsg={null}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div className="hrms-admin-field-cell">
                  <Labelbox
                    type="select"
                    labelname="Restricted / optional"
                    dropdown={RESTRICTED_DROPDOWN}
                    value={holidayForm.isRestrictedSelect}
                    changeData={(v) =>
                      setHolidayForm((s) => ({ ...s, isRestrictedSelect: v }))
                    }
                    required
                    error={false}
                    errmsg={null}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <button type="submit" className="hrms-admin-submit">
                  Add holiday
                </button>
              </Grid>
            </Grid>

            <div className="hrms-admin-holiday-ref">
              <h5 className="hrms-admin-holiday-ref__title">
                All holidays (India vs United States)
              </h5>
              <p className="hrms-dashboard-admin__hint hrms-admin-holiday-ref__hint">
                Full list from the dashboard API, split by region. Use it when
                adding IN or US holidays; the table below lists every holiday for
                edit/delete.
              </p>
              <div className="hrms-admin-holiday-ref__cols">
                <div className="hrms-admin-holiday-ref__col">
                  <h6>India (IN)</h6>
                  {refHolidaysIn.length === 0 ? (
                    <p className="hrms-admin-holiday-ref__empty">None listed.</p>
                  ) : (
                    <ul className="hrms-admin-holiday-ref__list hrms-admin-holiday-ref__scroll">
                      {refHolidaysIn.map((h, i) => (
                        <li key={h.holidayId || h.holiday_id || `in-${i}`}>
                          <span className="hrms-admin-holiday-ref__date">
                            {h.dateLabel || h.date_label || h.date || "—"}
                          </span>
                          {h.name || h.title}
                          {boolRestricted(h.isRestricted) ? (
                            <span className="hrms-dash-badge hrms-dash-badge--warn">
                              Optional
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="hrms-admin-holiday-ref__col">
                  <h6>United States (US)</h6>
                  {refHolidaysUs.length === 0 ? (
                    <p className="hrms-admin-holiday-ref__empty">None listed.</p>
                  ) : (
                    <ul className="hrms-admin-holiday-ref__list hrms-admin-holiday-ref__scroll">
                      {refHolidaysUs.map((h, i) => (
                        <li key={h.holidayId || h.holiday_id || `us-${i}`}>
                          <span className="hrms-admin-holiday-ref__date">
                            {h.dateLabel || h.date_label || h.date || "—"}
                          </span>
                          {h.name || h.title}
                          {boolRestricted(h.isRestricted) ? (
                            <span className="hrms-dash-badge hrms-dash-badge--warn">
                              Optional
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </form>
          <table className="hrms-admin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Title</th>
                <th>Region</th>
                <th>Type</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr key={h.holidayId || h.holiday_id}>
                  <td>{h.dateLabel || h.date}</td>
                  <td>{h.name || h.title}</td>
                  <td>{h.locationRegion || h.location_region || "—"}</td>
                  <td>
                    {boolRestricted(h.isRestricted) ? (
                      <span className="hrms-dash-badge hrms-dash-badge--warn">
                        Restricted
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="hrms-admin-linkbtn"
                      onClick={() => deleteHoliday(h.holidayId || h.holiday_id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adminTab === "Events" && (
        <div className="hrms-admin-panel">
          <form className="hrms-admin-form" onSubmit={onEventCreate}>
            <h4>New event</h4>
            <div className="hrms-admin-form__row">
              <label>
                Event date
                <input
                  required
                  type="date"
                  value={eventForm.eventDate}
                  onChange={(e) =>
                    setEventForm((s) => ({ ...s, eventDate: e.target.value }))
                  }
                />
              </label>
              <label>
                Title
                <input
                  required
                  value={eventForm.title}
                  onChange={(e) =>
                    setEventForm((s) => ({ ...s, title: e.target.value }))
                  }
                />
              </label>
              <label>
                End date (optional)
                <input
                  type="date"
                  value={eventForm.endDate}
                  onChange={(e) =>
                    setEventForm((s) => ({ ...s, endDate: e.target.value }))
                  }
                />
              </label>
              <label>
                Location (optional)
                <input
                  value={eventForm.location}
                  onChange={(e) =>
                    setEventForm((s) => ({ ...s, location: e.target.value }))
                  }
                />
              </label>
              <button type="submit" className="hrms-admin-submit">
                Add event
              </button>
            </div>
          </form>
          <table className="hrms-admin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Title</th>
                <th>Location</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.eventId || ev.event_id}>
                  <td>{ev.dateLabel || ev.date}</td>
                  <td>{ev.title}</td>
                  <td>{ev.location || "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="hrms-admin-linkbtn"
                      onClick={() => deleteEvent(ev.eventId || ev.event_id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adminTab === "Announcements" && (
        <div className="hrms-admin-panel">
          <form className="hrms-admin-form" onSubmit={onAnnounceCreate}>
            <h4>New announcement</h4>
            <div className="hrms-admin-form__col">
              <label>
                Title (optional)
                <input
                  value={announceForm.title}
                  onChange={(e) =>
                    setAnnounceForm((s) => ({ ...s, title: e.target.value }))
                  }
                />
              </label>
              <label>
                Body (required)
                <textarea
                  required
                  rows={3}
                  value={announceForm.body}
                  onChange={(e) =>
                    setAnnounceForm((s) => ({ ...s, body: e.target.value }))
                  }
                />
              </label>
              <button type="submit" className="hrms-admin-submit">
                Post announcement
              </button>
            </div>
          </form>
          <table className="hrms-admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Text</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {announcements.map((a) => (
                <tr key={a.announcementId || a.announcement_id}>
                  <td>{a.title || "—"}</td>
                  <td className="hrms-admin-clip">
                    {a.text || a.body || "—"}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="hrms-admin-linkbtn"
                      onClick={() =>
                        deleteAnnouncement(
                          a.announcementId || a.announcement_id,
                        )
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adminTab === "Appreciations" && (
        <div className="hrms-admin-panel">
          <form className="hrms-admin-form" onSubmit={onApprCreate}>
            <h4>New appreciation</h4>
            <Grid
              container
              spacing={2}
              alignItems="stretch"
              className="hrms-admin-appr-form"
            >
              <Grid item xs={12}>
                <div className="hrms-admin-field-cell hrms-admin-field-cell--textarea">
                  <Labelbox
                    type="textarea"
                    labelname="Message (required)"
                    rows={3}
                    value={apprForm.message}
                    changeData={(v) =>
                      setApprForm((s) => ({ ...s, message: v }))
                    }
                    required
                    error={false}
                    errmsg={null}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className="hrms-admin-field-cell">
                  <Labelbox
                    type="select"
                    labelname="To employee (optional)"
                    dropdown={employeeSelectDropdown}
                    value={
                      apprForm.toEmployeeId === "" ||
                      apprForm.toEmployeeId == null
                        ? ""
                        : String(apprForm.toEmployeeId)
                    }
                    changeData={(v) =>
                      setApprForm((s) => ({ ...s, toEmployeeId: v }))
                    }
                    error={false}
                    errmsg={null}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className="hrms-admin-field-cell">
                  <Labelbox
                    type="select"
                    labelname="From employee (optional)"
                    dropdown={employeeSelectDropdown}
                    value={
                      apprForm.fromEmployeeId === "" ||
                      apprForm.fromEmployeeId == null
                        ? ""
                        : String(apprForm.fromEmployeeId)
                    }
                    changeData={(v) =>
                      setApprForm((s) => ({ ...s, fromEmployeeId: v }))
                    }
                    error={false}
                    errmsg={null}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <button type="submit" className="hrms-admin-submit">
                  Post appreciation
                </button>
              </Grid>
            </Grid>
          </form>
          <table className="hrms-admin-table">
            <thead>
              <tr>
                <th>Message</th>
                <th>To employee</th>
                <th>From employee</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {appreciations.map((p) => (
                <tr key={p.appreciationId || p.appreciation_id}>
                  <td className="hrms-admin-clip">{p.text || p.message}</td>
                  <td>{getAppreciationToName(p, employeeById) || "—"}</td>
                  <td>{getAppreciationFromName(p, employeeById) || "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="hrms-admin-linkbtn"
                      onClick={() =>
                        deleteAppreciation(
                          p.appreciationId || p.appreciation_id,
                        )
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
